import type { ChallengeType, ChallengeMetric, ChallengeStatus, RecurrenceUnit, ChallengeTemplate, ChallengeDifficulty } from "./types";

/** i18n keys — resolve with t() at render time. */
export const CHALLENGE_TYPE_LABEL_KEYS: Record<ChallengeType, string> = {
  BEST_YOU_CAN: "challenges.type.BEST_YOU_CAN",
  HIT_THE_TARGET: "challenges.type.HIT_THE_TARGET",
};

export const CHALLENGE_METRIC_LABEL_KEYS: Record<ChallengeMetric, string> = {
  STEPS: "challenges.metric.STEPS",
  DURATION: "challenges.metric.DURATION",
  CALORIES: "challenges.metric.CALORIES",
  DISTANCE: "challenges.metric.DISTANCE",
};

export const CHALLENGE_METRIC_UNIT_KEYS: Record<ChallengeMetric, string> = {
  STEPS: "challenges.metricUnit.STEPS",
  DURATION: "challenges.metricUnit.DURATION",
  CALORIES: "challenges.metricUnit.CALORIES",
  DISTANCE: "challenges.metricUnit.DISTANCE",
};

export const CHALLENGE_STATUS_LABEL_KEYS: Record<ChallengeStatus, string> = {
  DRAFT: "challenges.statusDraft",
  UPCOMING: "challenges.statusUpcoming",
  ACTIVE: "challenges.statusActive",
  COMPLETED: "challenges.statusCompleted",
  CANCELLED: "challenges.statusCancelled",
};

export const CHALLENGE_STATUS_COLORS: Record<ChallengeStatus, string> = {
  DRAFT: "bg-muted text-muted-foreground",
  UPCOMING: "bg-warning/10 text-warning",
  ACTIVE: "bg-success/10 text-success",
  COMPLETED: "bg-secondary/10 text-secondary",
  CANCELLED: "bg-destructive/10 text-destructive",
};

export const CHALLENGE_TYPES: ChallengeType[] = ["BEST_YOU_CAN", "HIT_THE_TARGET"];

export const CHALLENGE_METRICS: ChallengeMetric[] = [
  "STEPS",
  "DURATION",
  "CALORIES",
  "DISTANCE",
];

export const RECURRENCE_UNITS: RecurrenceUnit[] = ["DAYS", "WEEKS", "MONTHS"];

export const RECURRENCE_UNIT_LABEL_KEYS: Record<RecurrenceUnit, string> = {
  DAYS: "shared.recurrenceUnit.DAYS",
  WEEKS: "shared.recurrenceUnit.WEEKS",
  MONTHS: "shared.recurrenceUnit.MONTHS",
};

// ─── Challenge Difficulty ────────────────────────────────────────────────────

export const DIFFICULTY_LABEL_KEYS: Record<ChallengeDifficulty, string> = {
  beginner: "challenges.difficulty.beginner",
  intermediate: "challenges.difficulty.intermediate",
  advanced: "challenges.difficulty.advanced",
};

export const DIFFICULTY_COLORS: Record<ChallengeDifficulty, string> = {
  beginner: "bg-green-500",
  intermediate: "bg-amber-500",
  advanced: "bg-orange-500",
};

// ─── Challenge Templates ─────────────────────────────────────────────────────

export const CHALLENGE_TEMPLATES: ChallengeTemplate[] = [
  {
    id: "10k-daily",
    name: "The 10K Daily",
    description:
      "Hit the WHO-recommended 10,000 steps every day for a week. A classic step goal that builds lasting habits.",
    tagline: "WHO-recommended daily step goal",
    emoji: "👟",
    difficulty: "beginner",
    type: "HIT_THE_TARGET",
    metric: "STEPS",
    targetValue: 10_000,
    durationDays: 7,
    entryFee: 0,
    rewards: [
      { fromRank: 1, toRank: 1, description: "€10 Amazon Gift Card" },
      { fromRank: 2, toRank: 2, description: "Coffee Voucher" },
      { fromRank: 3, toRank: 5, description: "Office Snack Box" },
    ],
    recurrence: { enabled: false, intervalValue: 1, intervalUnit: "WEEKS" },
  },
  {
    id: "workweek-hustle",
    name: "Workweek Hustle",
    description:
      "Monday to Friday step battle — who can move the most during the workweek? No weekend catch-up allowed!",
    tagline: "Monday-to-Friday step battle",
    emoji: "💼",
    difficulty: "intermediate",
    type: "BEST_YOU_CAN",
    metric: "STEPS",
    durationDays: 5,
    entryFee: 0,
    rewards: [
      { fromRank: 1, toRank: 1, description: "€15 Amazon Gift Card" },
      { fromRank: 2, toRank: 2, description: "Starbucks Gift Card (€5)" },
      { fromRank: 3, toRank: 5, description: "Branded Water Bottle" },
    ],
    recurrence: { enabled: false, intervalValue: 1, intervalUnit: "WEEKS" },
  },
  {
    id: "active-150",
    name: "Active 150",
    description:
      "Reach 150 active minutes this week — the WHO minimum for adult health. Any exercise counts!",
    tagline: "WHO 150 min/week activity guideline",
    emoji: "💪",
    difficulty: "beginner",
    type: "HIT_THE_TARGET",
    metric: "DURATION",
    targetValue: 150,
    durationDays: 7,
    entryFee: 0,
    rewards: [
      { fromRank: 1, toRank: 1, description: "€10 Amazon Gift Card" },
      { fromRank: 2, toRank: 2, description: "Smoothie Voucher" },
      { fromRank: 3, toRank: 5, description: "Fruit Basket" },
    ],
    recurrence: { enabled: false, intervalValue: 1, intervalUnit: "WEEKS" },
  },
  {
    id: "weekend-warrior",
    name: "Weekend Warrior",
    description:
      "A quick 3-day active-minutes sprint. Push your limits over the weekend and see who comes out on top!",
    tagline: "Quick burst active-minutes sprint",
    emoji: "⚡",
    difficulty: "intermediate",
    type: "BEST_YOU_CAN",
    metric: "DURATION",
    durationDays: 3,
    entryFee: 0,
    rewards: [
      { fromRank: 1, toRank: 1, description: "Leave 2h Early Friday" },
      { fromRank: 2, toRank: 2, description: "Coffee Voucher" },
      { fromRank: 3, toRank: 5, description: "Healthy Snack Bar Pack" },
    ],
    recurrence: { enabled: false, intervalValue: 1, intervalUnit: "WEEKS" },
  },
  {
    id: "calorie-crusher",
    name: "Calorie Crusher",
    description:
      "Burn as many calories as you can in a week. Any exercise type counts — running, cycling, yoga, or weightlifting!",
    tagline: "Burns across any exercise type",
    emoji: "🔥",
    difficulty: "intermediate",
    type: "BEST_YOU_CAN",
    metric: "CALORIES",
    durationDays: 7,
    entryFee: 0,
    rewards: [
      { fromRank: 1, toRank: 1, description: "€15 Sports Store Voucher" },
      { fromRank: 2, toRank: 2, description: "Protein Shake Set" },
      { fromRank: 3, toRank: 5, description: "Resistance Band Kit" },
    ],
    recurrence: { enabled: false, intervalValue: 1, intervalUnit: "WEEKS" },
  },
  {
    id: "monthly-marathon",
    name: "Monthly Marathon",
    description:
      "Walk or run a full marathon distance (42.195 km) spread over 30 days. Pace yourself — consistency wins!",
    tagline: "Walk/run a marathon over 30 days",
    emoji: "🏅",
    difficulty: "advanced",
    type: "HIT_THE_TARGET",
    metric: "DISTANCE",
    targetValue: 42.195,
    durationDays: 30,
    entryFee: 0,
    rewards: [
      { fromRank: 1, toRank: 1, description: "Half-Day PTO" },
      { fromRank: 2, toRank: 2, description: "€10 Amazon Gift Card" },
      { fromRank: 3, toRank: 5, description: "Company Branded T-Shirt" },
    ],
    recurrence: { enabled: false, intervalValue: 1, intervalUnit: "WEEKS" },
  },
  {
    id: "distance-dash",
    name: "Distance Dash",
    description:
      "Two-week competitive distance race. Cover as many kilometres as you can — every step, run, and ride counts!",
    tagline: "Two-week competitive distance race",
    emoji: "🏃",
    difficulty: "intermediate",
    type: "BEST_YOU_CAN",
    metric: "DISTANCE",
    durationDays: 14,
    entryFee: 0,
    rewards: [
      { fromRank: 1, toRank: 1, description: "VIP Parking — 1 Week" },
      { fromRank: 2, toRank: 2, description: "Starbucks Gift Card (€5)" },
      { fromRank: 3, toRank: 5, description: "Branded Notebook & Pen Set" },
    ],
    recurrence: { enabled: false, intervalValue: 1, intervalUnit: "WEEKS" },
  },
];
